// Cursor-following spotlight: writes the pointer position into two CSS custom
// properties on the section root (throttled to one write per animation frame)
// and resets them to a resting position when the pointer leaves.
//
//   const spotlight = usePointerSpotlight(rootRef, { xVar: "--cfa-x", yVar: "--cfa-y", restX: "50%", restY: "45%" });
//   <section ref={rootRef} {...spotlight} />
//
// Skipped on touch devices and (optionally) when the user prefers reduced motion.
import { useCallback, useEffect, useRef } from "react";

function pointerRestricted(respectReducedMotion) {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  if (window.matchMedia("(pointer: coarse)").matches) return true;
  return respectReducedMotion && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function usePointerSpotlight(rootRef, { xVar, yVar, restX, restY, respectReducedMotion = false }) {
  const frame = useRef(null);

  useEffect(() => () => window.cancelAnimationFrame(frame.current), []);

  const onPointerMove = useCallback(
    (event) => {
      const root = rootRef.current;
      if (!root || pointerRestricted(respectReducedMotion)) return;
      window.cancelAnimationFrame(frame.current);
      frame.current = window.requestAnimationFrame(() => {
        const rect = root.getBoundingClientRect();
        root.style.setProperty(xVar, `${event.clientX - rect.left}px`);
        root.style.setProperty(yVar, `${event.clientY - rect.top}px`);
      });
    },
    [rootRef, xVar, yVar, respectReducedMotion]
  );

  const onPointerLeave = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    root.style.setProperty(xVar, restX);
    root.style.setProperty(yVar, restY);
  }, [rootRef, xVar, yVar, restX, restY]);

  return { onPointerMove, onPointerLeave };
}
